import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/styles";
import Tooltip from "@material-ui/core/Tooltip";
import {
  checkIfEmpty,
  checkIfInteger,
  checkIfInRange,
  checkIfDecimal,
} from "../../helpers/validation";

const SpecialTextField = ({
  value,
  onChange,
  label,
  inputProps = {},
  onBlur,
  endadornment = null,
  altlabel = null,
  showArrows = true,
  showCustomError = null,
  customMessageError = null,
  customValidation = null,
  setFieldErrors = null,
  showTooltip = true,
  setHasErrors = null,
  children = null,
  ...props
}) => {
  const inputClasses = useCustomInputStyles({ showArrows });
  const [validationErrorMessage, setValidationErrorMessage] = useState();
  const fieldNameRef = useRef(null);
  const useStyles = makeStyles((_) => ({
    root: {
      width: altlabel ? "120px" : "80px",
    },
  }));
  const classes = useStyles();

  const handleChange = (e) => {
    if (e.target.value === "") {
      onChange(null);
    } else {
      onChange(e.target.value);
    }
  };

  const handleBlur = (e) => {
    onBlur(e.target.value);
    if (validateField) {
      validateField(e, true);
    }
  };

  const validateField = (e, shouldUpdateValue = true) => {
    let containsErrors = false;
    if (customValidation !== null) {
      const { range, validateEmpty, checkIfInt, checkIfDec } = customValidation;

      let re = /^$|^[0-9\b]+$/;

      if (checkIfDec || (!checkIfDec && !checkIfInt)) {
        re = /^$|^[0-9\b]+(\.[0-9]{0,2})?$/;
      }

      if (!re.test(e.target.value)) {
        return;
      }

      const duration = parseInt(e.target.value, 10);
      if (range) {
        let errors = [checkIfInRange(duration, range, checkIfDec)];

        if (validateEmpty) {
          errors.unshift(checkIfEmpty(e.target.value));
        }

        if (checkIfInt) {
          errors.push(checkIfInteger(e.target.value));
        }

        if (checkIfDec) {
          errors.push(checkIfDecimal(e.target.value));
        }

        errors = errors.filter((error) => error !== "");
        if (errors.length > 0) {
          containsErrors = true;
          if (typeof setHasErrors === "function") {
            setHasErrors(errors);
          }

          if (typeof setFieldErrors === "function") {
            setFieldErrors((prevState) => ({
              ...prevState,
              [e.target?.name]: {
                ...prevState[e.target?.name],
                errors: errors,
              },
            }));
          }
        } else {
          if (typeof setHasErrors === "function") {
            setHasErrors([]);
          }
        }
        setValidationErrorMessage(errors.join("  "));
      }
    }

    if (!containsErrors && typeof setFieldErrors === "function") {
      setFieldErrors((prevState) => {
        const newState = { ...prevState };
        delete newState[e.target?.name];
        return newState;
      });
    }

    if (shouldUpdateValue) {
      handleChange(e);
    }
  };

  function handleFirstChange(event) {
    let value = event.target.value;
    if (value && value.toString().startsWith(".")) {
      value = "0" + value;
    }

    // Limit to 2 decimal places
    if (value && value.includes(".")) {
      const parts = value.split(".");
      if (parts[1] && parts[1].length > 2) {
        value = `${parts[0]}.${parts[1].substring(0, 2)}`;
      }
    }

    const modifiedEvent = {
      ...event,
      target: {
        ...event.target,
        value: value,
      },
    };

    if (onChange) {
      onChange(modifiedEvent);
    }
    if (validateField) {
      validateField(modifiedEvent);
    }
  }

  const handleInvalid = (e) => {
    e.preventDefault();
    e.target.setCustomValidity("");
  };

  // Re-validate when customValidation changes (e.g., when dynamic ranges change)
  useEffect(() => {
    // Just validate if there is a value and customValidation is defined
    if (value !== null && value !== "" && customValidation !== null) {
      const fieldName = label?.trim().replace(/\s+/g, "").toLowerCase();
      fieldNameRef.current = fieldName;
      
      // Create a synthetic event to pass to the validation function
      const syntheticEvent = {
        target: {
          value: value?.toString() || "",
          name: fieldName,
        },
      };
      
      // Execute validation without updating the value
      validateField(syntheticEvent, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customValidation?.range?.[0], customValidation?.range?.[1], value]);

  return (
    <Container hasMargin={label !== undefined}>
      <Tooltip
        title={
          showCustomError
            ? customMessageError || ""
            : showTooltip && validationErrorMessage
            ? validationErrorMessage
            : ""
        }
        open={showCustomError || validationErrorMessage ? true : false}
        placement="top"
        classes={{ tooltip: inputClasses.customTooltip }}
        PopperProps={{
          className: showCustomError ? inputClasses.customPopper : undefined,
          popperOptions: {
            modifiers: [
              {
                name: "computeStyles",
                enabled: true,
                phase: "write",
                fn: (data) => {
                  if (data && data.offsets && data.offsets.popper) {
                    data.offsets.popper.top = data.offsets.reference.top - 45;
                    data.offsets.popper.left = data.offsets.reference.left;
                  }
                  return data;
                },
              },
            ],
          },
        }}
      >
        <TextField
          name={label?.trim().replace(/\s+/g, "").toLowerCase()}
          style={{
            background: props.disabled ? "#DADEE3" : "#ffffff",
            opacity: props.disabled ? 0.5 : 1,
          }}
          type="number"
          value={value ?? ""}
          variant="outlined"
          classes={classes}
          InputProps={{
            classes: {
              input: inputClasses.input,
              root: inputClasses.root
            },
          }}
          inputProps={{
            min: 1,
            type: "number",
            onInvalid: handleInvalid,
            title: "",
            ...inputProps,
          }}
          disabled={props.disabled}
          {...props}
          onChange={handleFirstChange}
          onBlur={onBlur && handleBlur}
          label={altlabel}
        />
      </Tooltip>

      {label && (
        <Typography
          style={{
            marginLeft: "1em",
            display: "flex",
          }}
        >
          {label}
          {endadornment}
          {props.required && (
            <span
              style={{
                color: "red",
                marginLeft: "5px",
                marginRight: "5px",
              }}
            >
              *
            </span>
          )}
        </Typography>
      )}
      {children}
    </Container>
  );
};

export default SpecialTextField;

const Container = styled.div`
  display: flex;
  align-items: center;
  height: fit-content;
  margin-right: ${(p) => (p.hasMargin ? "1em" : 0)};
`;

const useCustomInputStyles = makeStyles({
  input: {
    textAlign: "center",
  },
  root: (props) => ({
    '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
      {
        "-webkit-appearance": props.showArrows ? "" : "none",
        margin: 0,
      },
    '& input[type="number"]': {
      "-moz-appearance": props.showArrows ? "" : "textfield",
    },
  }),
  customTooltip: {
    backgroundColor: "white",
    color: "red",
    border: "1px solid #dadde9",
    fontSize: "14px",
  },
  customPopper: {
    backgroundColor: "transparent",
    border: "none",
  },
});
